import { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { AlertTriangle, HelpCircle, Trash2, RefreshCw } from "lucide-react";

/**
 * Branded confirmation modal — drop-in replacement for `window.confirm()`.
 *
 * Two patterns:
 *   (1) Controlled: pass `open` + `onOpenChange` and drive from parent state.
 *   (2) Imperative: use the `useConfirm()` hook + <ConfirmDialogHost />, then
 *       `const ok = await confirm({ title, description })`. No render plumbing
 *       required at the callsite — mirrors the ergonomics of window.confirm.
 *
 * Variants:
 *   - "danger"  → red confirm button + AlertTriangle. Deletes, revokes, etc.
 *   - "warning" → amber confirm button + AlertTriangle. Destructive-ish
 *                 (force resync, archive) where recovery is possible.
 *   - "default" → teal confirm button + HelpCircle. Neutral confirmations.
 */
const VARIANT_STYLES = {
  danger:  { Icon: Trash2,         iconClass: "text-rose-500",   btnClass: "bg-rose-500 hover:bg-rose-600 text-white" },
  warning: { Icon: AlertTriangle,  iconClass: "text-amber-500",  btnClass: "bg-amber-500 hover:bg-amber-600 text-white" },
  resync:  { Icon: RefreshCw,      iconClass: "text-amber-500",  btnClass: "bg-amber-500 hover:bg-amber-600 text-white" },
  default: { Icon: HelpCircle,     iconClass: "text-[#0D9488]",  btnClass: "bg-[#0D9488] hover:bg-[#14B8A6] text-white" },
};

export const ConfirmDialog = ({
  open,
  onOpenChange,
  title = "Are you sure?",
  description = "",
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  variant = "default",
  loading = false,
  children,
}) => {
  const meta = VARIANT_STYLES[variant] || VARIANT_STYLES.default;
  const Icon = meta.Icon;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Icon className={`w-5 h-5 ${meta.iconClass}`} /> {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="pt-1 text-sm text-slate-600 leading-relaxed">
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        {children && <div className="py-1">{children}</div>}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            data-testid="confirm-dialog-cancel"
          >
            {cancelText}
          </Button>
          <Button
            className={meta.btnClass}
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-dialog-confirm"
          >
            {loading ? "Working…" : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -----------------------------------------------------------------------------
// Imperative API — `const ok = await confirm({...})`. Requires mounting a
// single <ConfirmDialogHost /> near the app root (already done in App.js).
// -----------------------------------------------------------------------------
let _openConfirmFn = null;

export const confirm = (opts) => {
  if (!_openConfirmFn) {
    // Safety fallback for callers used before the host mounts (SSR / tests) —
    // preserves window.confirm semantics so nothing breaks.
    // eslint-disable-next-line no-alert
    return Promise.resolve(window.confirm(opts?.description || opts?.title || "Are you sure?"));
  }
  return _openConfirmFn(opts);
};

export const ConfirmDialogHost = () => {
  const [state, setState] = useState({ open: false, opts: {}, resolve: null });

  // Register the singleton opener once. The parent effect (App.js) mounts a
  // single host, so this closure captures the latest setState.
  _openConfirmFn = (opts) =>
    new Promise((resolve) => setState({ open: true, opts: opts || {}, resolve }));

  const close = (result) => {
    if (state.resolve) state.resolve(result);
    setState({ open: false, opts: {}, resolve: null });
  };

  return (
    <ConfirmDialog
      open={state.open}
      onOpenChange={(open) => { if (!open) close(false); }}
      onConfirm={() => close(true)}
      title={state.opts.title}
      description={state.opts.description}
      confirmText={state.opts.confirmText}
      cancelText={state.opts.cancelText}
      variant={state.opts.variant}
    />
  );
};
